import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import '../components/cos-icon.js';
import { fieldAnatomyStyles, fieldMessage, nextFieldMessageId, renderFieldLabel, renderFieldMessage } from './field-anatomy.js';
import { fieldHelpDockActiveContext } from './field-help-dock-context.js';

export type CosInputType =
	| 'text' | 'email' | 'password' | 'number' | 'search' | 'url' | 'tel'
	// Native date/time passthroughs (wave 3, ds-unlocks) — the OS-drawn
	// picker is the UI, same rationale as cos-select's WHY note but
	// inverted: the native date/time picker is GOOD and unstyleable
	// anyway, so there's no design-system value in replacing it.
	| 'date' | 'time' | 'datetime-local'
	// Native color swatch passthrough (selects-everywhere) — same rationale:
	// the OS-drawn color picker is good and unstyleable anyway.
	| 'color';

/**
 * <cos-input> — the house single-line text field.
 *
 * WHY THIS EXISTS: 286 files hand-roll a raw `<input>`, each inventing its
 * own padding/border/focus-ring/error treatment — so a text field sitting
 * next to a `cos-select` in the same form row rarely matches it. This is
 * the single source: the same trigger geometry as `cos-select` (padding,
 * border, radius, focus ring) so the two read as siblings, plus the
 * label/help/error/disabled/required anatomy every field needs.
 *
 * Controlled, like `cos-select`: `value` is the prop the consumer owns.
 * Internally the component echoes the DOM value back onto `value` on
 * every keystroke — so the field stays visually correct without the
 * consumer having to round-trip on every character — and fires
 * `cos:input` for that. `cos:change` fires on COMMIT (blur, or Enter),
 * which is the moment a consumer typically persists, and only when the
 * value actually changed since the field was focused (so tabbing through
 * an untouched field is silent).
 *
 * Props:
 *   - type: text|email|password|number|search|url|tel (default text)
 *   - value: string — controlled
 *   - label?, placeholder?, helpText?
 *   - error?: string — when set, replaces helpText with error styling +
 *     message, and marks the field aria-invalid
 *   - required?, disabled?
 *   - size: sm|md (default md — matches cos-select's one size, so the two
 *     line up in a row)
 *   - name?, autocomplete?
 *   - hideLabel?: boolean (wave 3, ds-unlocks) — `label` stays REQUIRED
 *     (it's still the control's accessible name via the wrapping
 *     `<label>`), but the visible text is clipped off-screen. For a
 *     placeholder-only search/filter field that has no room for a
 *     visible caption but still needs a real accessible name.
 *   - maxlength?: number (wave 6, ds-unlocks) — plain passthrough to the
 *     inner `<input maxlength>`; 0 (default) omits the attribute.
 *   - min?, max?: string (wave 6, ds-unlocks) — plain passthrough to the
 *     inner `<input min/max>` (a date floor/ceiling for type="date", a
 *     numeric bound for type="number"); '' (default) omits the attribute.
 *   - step?: string (selects-everywhere) — plain passthrough to the inner
 *     `<input step>` (a decimal increment for type="number", e.g. "0.01"
 *     for currency); '' (default) omits the attribute.
 *   - clearable?: boolean (ds-input-iconclear) — when set and the field
 *     carries a value, an inline × button renders inside the field on the
 *     right. Clicking it empties the value, fires BOTH `cos:input` and
 *     `cos:change` with `{value: ''}` (a removal is also a commit — unlike
 *     a keystroke, there's no reason to wait), and refocuses the field.
 *     Hidden when the field is empty or disabled. Mirrors
 *     `cos-date-picker`'s trigger-level clear button.
 *   - `slot="leading"` (ds-input-iconclear) — an optional leading glyph
 *     (typically a `<cos-icon>`) rendered inside the field chrome, left of
 *     the text. Retires the "absolutely-positioned icon over a raw input"
 *     shape several consumers hand-rolled (see
 *     component-vocabulary.md — "Composition patterns"). When `clearable`
 *     and the leading slot are both used, the field reads icon — text — ×,
 *     the same anatomy as `cos-date-picker`'s trigger.
 *
 * Emits `cos:input` (detail: { value }) on every keystroke and
 * `cos:change` (detail: { value }) on commit (blur or Enter).
 */
@customElement('cos-input')
export class CosInput extends LitElement {
	@property({ type: String }) type: CosInputType = 'text';
	@property({ type: String }) value = '';
	@property({ type: String }) label = '';
	@property({ type: String }) placeholder = '';
	@property({ type: String }) helpText = '';
	@property({ type: String }) error = '';
	/** Width of the input box itself, e.g. "120px" or "8ch". Set this
	 *  rather than sizing the host when the field has helpText: the host
	 *  governs how wide the message is allowed to be, so narrowing it
	 *  wraps the help into a column a few words wide. */
	@property({ type: String, attribute: 'control-width', reflect: true }) controlWidth = '';
	@property({ type: Boolean }) required = false;
	@property({ type: Boolean, reflect: true }) disabled = false;
	// Read-only keeps the text selectable and copyable, which disabled does not.
	@property({ type: Boolean, reflect: true }) readonly = false;
	@property({ type: String, reflect: true }) size: 'sm' | 'md' = 'md';
	@property({ type: String }) name = '';
	@property({ type: String }) autocomplete = '';
	@property({ type: Boolean, attribute: 'hide-label' }) hideLabel = false;
	@property({ type: Number }) maxlength = 0;
	@property({ type: String }) min = '';
	@property({ type: String }) max = '';
	@property({ type: String }) step = '';
	@property({ type: Boolean }) clearable = false;
	/**
	 * Additive and unset by default (every other field renders exactly as before): when
	 * a consumer draws a list of offers beside the field, the input says it is a
	 * combobox and whether the list is open. The list lives outside this shadow root, so
	 * no aria-controls or aria-activedescendant can point at it; the consumer announces
	 * the highlighted offer itself.
	 */
	@property({ attribute: false }) combobox?: { expanded: boolean };

	@consume({ context: fieldHelpDockActiveContext, subscribe: true })
	@state()
	private _dockActive = false;

	@query('input') private _inputEl?: HTMLInputElement;

	// Snapshot of `value` taken on focus — lets commit-on-blur/Enter skip
	// firing cos:change when nothing actually changed (tabbing through an
	// untouched field, or pressing Enter twice).
	private _focusValue = '';

	private readonly _msgId = nextFieldMessageId('cos-input');

	/** Programmatic focus — a consumer opening a modal often wants this. */
	focus(): void {
		this._inputEl?.focus();
	}

	private _onFocus(): void {
		this._focusValue = this.value;
	}

	private _onInput(e: Event): void {
		this.value = (e.target as HTMLInputElement).value;
		this.dispatchEvent(
			new CustomEvent('cos:input', { detail: { value: this.value }, bubbles: true, composed: true }),
		);
	}

	private _commit(): void {
		if (this.value === this._focusValue) return;
		this._focusValue = this.value;
		this.dispatchEvent(
			new CustomEvent('cos:change', { detail: { value: this.value }, bubbles: true, composed: true }),
		);
	}

	private _onKeydown(e: KeyboardEvent): void {
		if (e.key === 'Enter') this._commit();
	}

	// type=number changes its value on mouse-wheel/trackpad scroll while
	// focused -- a single trackpad swipe fires many wheel events, each
	// nudging the value by one step, so the number runs away before the
	// user can stop it (feedback #4249). Blurring synchronously, before the
	// browser applies the wheel's default action, suppresses the change for
	// this event too; once unfocused, scrolling over the field behaves like
	// any other page content again.
	private _onWheel(): void {
		if (this.type === 'number') this._inputEl?.blur();
	}

	// A removal gesture, not a draft edit — mirrors cos-date-picker's
	// trigger-level clear: emits immediately (both cos:input and cos:change,
	// since a clear IS a commit) and refocuses the field rather than leaving
	// focus on a button that's about to disappear (the button's visibility
	// is gated on a non-empty value, so it unmounts on the next render).
	private _onClear(e: MouseEvent): void {
		e.preventDefault();
		if (this.disabled) return;
		this.value = '';
		this._focusValue = '';
		this.dispatchEvent(new CustomEvent('cos:input', { detail: { value: '' }, bubbles: true, composed: true }));
		this.dispatchEvent(new CustomEvent('cos:change', { detail: { value: '' }, bubbles: true, composed: true }));
		this._inputEl?.focus();
	}

	// A direct-children scan (matching how the platform itself resolves slot
	// assignment — a `slot` attribute only counts on a light-DOM child of the
	// host) rather than cached state: cheap, and correct on every render
	// without needing to special-case the very first one before any
	// `slotchange` has had a chance to fire.
	private _hasLeading(): boolean {
		return Array.from(this.children).some((el) => el.getAttribute('slot') === 'leading');
	}

	static styles = css`
		${fieldAnatomyStyles}
		:host {
			display: block;
			width: 100%;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-primary);
		}
		/* A narrow box does not mean narrow help text. Consumers size the
		   HOST to size the box (a percentage wants ~120px), which squeezes
		   any helpText into the same column and wraps it to one or two
		   words a line. Set control-width instead: it sizes the box on its
		   own (applied inline in render), leaving the host as wide as its
		   container so the message stays readable underneath. */
		.field {
			display: flex;
			flex-direction: column;
			gap: 6px;
			width: 100%;
		}
		/* The bordered box moved from .control (the bare input) onto this
		   wrapper -- the anatomy the leading slot and the clear button need to
		   render INSIDE the field chrome (the cos-date-picker .trigger
		   precedent), rather than each consumer absolutely-positioning its own
		   icon/clear over a raw input. With neither slotted nor clearable, the
		   wrapper contributes nothing new: same padding, border, and radius the
		   bare .control carried before. */
		.control-wrap {
			display: flex;
			align-items: center;
			gap: var(--cos-space-2);
			width: 100%;
			box-sizing: border-box;
			padding: 9px 12px;
			background: var(--cos-surface);
			border: 1.5px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
		}
		:host([size='sm']) .control-wrap {
			padding: 7px 12px;
		}
		:host(:not([disabled])) .control-wrap:hover {
			border-color: var(--cos-brand-500);
		}
		.control-wrap:focus-within {
			outline: none;
			border-color: var(--cos-brand-700);
			box-shadow: var(--cos-shadow-focus);
		}
		.control-wrap:has(.control[aria-invalid='true']) {
			border-color: var(--cos-status-danger-text);
		}
		:host([disabled]) .control-wrap {
			opacity: 0.6;
		}
		.leading {
			display: none;
			flex: none;
			align-items: center;
			color: var(--cos-text-tertiary);
		}
		.leading.has-content {
			display: inline-flex;
		}
		.control {
			flex: 1;
			min-width: 0;
			width: 100%;
			box-sizing: border-box;
			padding: 0;
			border: 0;
			outline: 0;
			font: inherit;
			color: var(--cos-text-primary);
			background: transparent;
		}
		:host([size='sm']) .control {
			font-size: var(--cos-font-size-xs);
		}
		.control::placeholder {
			color: var(--cos-text-tertiary);
		}
		.control:disabled {
			cursor: not-allowed;
		}
		/* The custom clearable × is the one clear affordance -- suppress
		   Safari/Chrome's native type=search cancel button so the two never
		   double up. Scoped to the .clearable class (not every type=search
		   field) so a consumer that hasn't opted into clearable keeps whatever
		   it had before. */
		.control-wrap.clearable .control[type='search']::-webkit-search-cancel-button {
			-webkit-appearance: none;
			appearance: none;
			display: none;
		}
		.clear-btn {
			flex: none;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			border: 0;
			background: transparent;
			padding: 2px;
			color: var(--cos-text-tertiary);
			border-radius: var(--cos-radius-sm);
			cursor: pointer;
		}
		.clear-btn:hover {
			background: var(--cos-surface-raised);
		}
		.clear-btn:focus-visible {
			outline: none;
			box-shadow: var(--cos-shadow-focus);
		}
		/* Workstream A1 — a focused input under 16px triggers iOS Safari's
		   forced focus-zoom (the whole page zooms in, then has to be zoomed
		   back out — a jarring, form-breaking experience on a touch device).
		   max() never shrinks the token below 16px but still lets a larger
		   token (--cos-font-size-md, say) win on its own. The size='sm'
		   selector is repeated here at matching specificity — a bare
		   .control inside this media query alone would lose the cascade to
		   :host([size='sm']) .control above regardless of source order,
		   defeating the fix on exactly the size where it matters. */
		@media (pointer: coarse) {
			.control,
			:host([size='sm']) .control {
				font-size: max(16px, var(--cos-font-size-base));
			}
		}
	`;

	render() {
		const { message, isError } = fieldMessage(this.helpText, this.error);
		const showClear = this.clearable && !this.disabled && this.value !== '';
		return html`
			<label class="field">
				${renderFieldLabel({ label: this.label, required: this.required, hideLabel: this.hideLabel })}
				<div
					class="control-wrap${this.clearable ? ' clearable' : ''}"
					style=${this.controlWidth ? `width:${this.controlWidth}` : nothing}
				>
					<slot
						name="leading"
						class=${this._hasLeading() ? 'leading has-content' : 'leading'}
						@slotchange=${() => this.requestUpdate()}
					></slot>
					<input
						class="control"
						type=${this.type}
						name=${this.name || nothing}
						autocomplete=${this.autocomplete || nothing}
						placeholder=${this.placeholder || nothing}
						maxlength=${this.maxlength || nothing}
						min=${this.min || nothing}
						max=${this.max || nothing}
						step=${this.step || nothing}
						.value=${this.value}
						?required=${this.required}
						?disabled=${this.disabled}
						?readonly=${this.readonly}
						aria-invalid=${this.error ? 'true' : 'false'}
						role=${this.combobox ? 'combobox' : nothing}
						aria-autocomplete=${this.combobox ? 'list' : nothing}
						aria-haspopup=${this.combobox ? 'listbox' : nothing}
						aria-expanded=${this.combobox ? (this.combobox.expanded ? 'true' : 'false') : nothing}
						aria-describedby=${message ? this._msgId : nothing}
						@focus=${this._onFocus}
						@input=${this._onInput}
						@keydown=${this._onKeydown}
						@wheel=${this._onWheel}
						@blur=${this._commit}
					/>
					${showClear
						? html`<button type="button" class="clear-btn" aria-label="Clear" @click=${(e: MouseEvent) => this._onClear(e)}>
								<cos-icon name="x" size="13"></cos-icon>
							</button>`
						: nothing}
				</div>
				${renderFieldMessage({ message, isError, id: message ? this._msgId : undefined, visuallyHidden: this._dockActive && !isError })}
			</label>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-input': CosInput;
	}
}
